import { Tree } from '../types/Tree';

interface QuestionAnswer {
  answer: string;
  followUpQuestions: string[];
}

export const getQuestionAnswer = (question: string, tree: Tree): QuestionAnswer => {
  const q = question.toLowerCase();
  
  // Eucalyptus specific answers
  if (tree.id === 'eucalyptus') {
    if (q.includes('smell') || q.includes('scent')) {
      return {
        answer: "Eucalyptus trees smell strong because their leaves contain special oils called eucalyptol! This oil helps protect the tree from insects and diseases. It's the same smell you might recognize from cough drops or cleaning products. The tree makes this oil in tiny pockets in its leaves, and when the wind blows or you crush a leaf, the smell comes out!",
        followUpQuestions: [
          "How do other animals react to the eucalyptus smell?",
          "Can humans use eucalyptus oil for medicine?",
          "Do all eucalyptus trees smell the same?"
        ]
      };
    }
    if ((q.includes('hang') && q.includes('down')) || (q.includes('leaves') && q.includes('down'))) {
      return {
        answer: "Eucalyptus leaves hang down because of a clever survival trick! The leaves twist on their stems so both sides face the sun equally, which helps them avoid getting too hot. This hanging position also helps the tree save water - less surface area is exposed to the drying wind and sun. Plus, when leaves hang down, rainwater drips off quickly, preventing harmful fungi from growing on them!",
        followUpQuestions: [
          "How do eucalyptus leaves compare to other tree leaves?",
          "Do eucalyptus leaves change position during the day?",
          "Why don't all trees have hanging leaves?"
        ]
      };
    }
    if (q.includes('koala') || q.includes('australia')) {
      return {
        answer: "Koalas are the eucalyptus experts! They can eat eucalyptus leaves that would make other animals sick. Koalas have special bacteria in their stomachs that help them digest the tough, oily leaves. Baby koalas even eat their mom's poop to get these helpful bacteria! Even though we have eucalyptus trees in California, koalas only live wild in Australia.",
        followUpQuestions: [
          "Why can't other animals eat eucalyptus leaves?",
          "How do koalas choose which leaves to eat?",
          "What would happen if we brought koalas to California?"
        ]
      };
    }
  }
  
  // Oak specific answers
  if (tree.id === 'oak') {
    if (q.includes('acorn')) {
      return {
        answer: "Acorns are like nature's snack bars! They're packed with nutrients that feed squirrels, deer, birds, and many other animals. One big oak tree can make 10,000 acorns in a good year! Acorn woodpeckers are especially clever - they drill thousands of holes in oak bark to store acorns for winter, like a giant pantry. Native Americans also used to grind acorns into flour for bread.",
        followUpQuestions: [
          "How do animals know which acorns are good to eat?",
          "Why don't all acorns grow into new oak trees?",
          "How did Native Americans remove the bitter taste from acorns?"
        ]
      };
    }
    if (q.includes('strong') || q.includes('storm')) {
      return {
        answer: "Oak trees are incredibly strong because they grow slowly and develop deep, spreading roots that anchor them like underground arms. Their wood is dense and hard, which is why it's been used to build ships and houses for thousands of years! During storms, oak trees can bend without breaking because their branches are flexible. Their deep roots also help them find water during dry times.",
        followUpQuestions: [
          "How deep do oak tree roots go?",
          "Why do some trees break in storms but oaks don't?",
          "How old can oak trees get?"
        ]
      };
    }
    if (q.includes('ecosystem') || q.includes('help') || q.includes('value')) {
      return {
        answer: "California oak trees are ecosystem superstars! They support over 300 species of wildlife - more than any other tree in California. Their deep roots bring up water and nutrients from deep underground, which helps other plants grow nearby. The acorns feed countless animals, and the tree's canopy creates cool, shady spots where different plants can thrive. They also prevent soil erosion with their strong root system.",
        followUpQuestions: [
          "Which animals depend most on oak trees?",
          "How do oak trees help during droughts?",
          "What happens to the forest when oak trees are removed?"
        ]
      };
    }
  }
  
  // Cypress specific answers
  if (tree.id === 'cypress') {
    if (q.includes('shape') || q.includes('twisted') || q.includes('wind')) {
      return {
        answer: "Cypress trees grow in amazing twisted shapes because of the constant ocean wind! When they're young, the wind pushes on them every day, so they learn to grow in the direction that helps them survive. It's like they're doing yoga in slow motion! The salt spray from the ocean also affects how they grow. Each tree becomes a unique sculpture shaped by years of wind and weather.",
        followUpQuestions: [
          "Do cypress trees in calm places grow differently?",
          "How long does it take for a cypress to get its twisted shape?",
          "Can other trees survive ocean wind like cypress trees do?"
        ]
      };
    }
    if (q.includes('monarch') || q.includes('butterfly')) {
      return {
        answer: "Monarch butterflies choose cypress trees for their winter vacation! The trees provide perfect shelter from cold winds and rain. Thousands of monarchs cluster together on cypress branches to stay warm, like a living orange blanket! The trees are also close to the coast where the temperature stays mild. Monarchs travel thousands of miles to reach these special cypress groves in California.",
        followUpQuestions: [
          "How do monarchs find the same trees every year?",
          "What do monarchs eat when they're staying in cypress trees?",
          "Where do the monarchs go when winter is over?"
        ]
      };
    }
    if (q.includes('salt') || q.includes('ocean') || q.includes('survive')) {
      return {
        answer: "Cypress trees are like ocean superheroes! They can survive salty ocean air because they have special waxy coatings on their leaves that protect them from salt damage. Their roots are also adapted to handle soil that sometimes gets splashed with salty water. Most trees would die in these conditions, but cypress trees actually thrive! They even help filter the salt spray, making it easier for other plants to grow behind them.",
        followUpQuestions: [
          "What happens to trees that can't handle salt?",
          "How do cypress trees help other plants near the ocean?",
          "Do cypress trees need salt water to survive?"
        ]
      };
    }
  }
  
  // General ecosystem questions
  if (q.includes('ecosystem') || q.includes('environment') || q.includes('help')) {
    return {
      answer: `${tree.name} helps the environment in many ways! ${tree.ecosystemValue.join(', ')}. Trees are like nature's superheroes - they clean the air we breathe, provide homes for animals, and help prevent soil erosion. Each tree is like a small city where many different creatures live together!`,
      followUpQuestions: [
        "How do trees clean the air?",
        "What happens to an ecosystem when trees are removed?",
        "How many animals can live in one tree?"
      ]
    };
  }
  
  // Animals and wildlife
  if (q.includes('animal') || q.includes('bird') || q.includes('insect')) {
    const birdNames = tree.wildlife.birds.map(bird => bird.name);
    const insectNames = tree.wildlife.insects.map(insect => insect.name);
    return {
      answer: `Many amazing creatures call ${tree.name} home! Birds like ${birdNames.join(', ')} find food and shelter here. Insects such as ${insectNames.join(', ')} also depend on this tree. Each animal has a special job - birds spread seeds, insects help with pollination, and everyone helps keep the tree healthy!`,
      followUpQuestions: [
        "How do birds help trees grow?",
        "Why are insects important for trees?",
        "What do these animals do when it's not tree season?"
      ]
    };
  }
  
  // Default response for unmatched questions
  return {
    answer: `That's a great question about ${tree.name}! Trees are fascinating living things that have been growing on Earth for millions of years. ${tree.interestingFacts[0]} Every tree has its own special story and role in nature. What specific part of the tree interests you most?`,
    followUpQuestions: [
      "How old can this tree get?",
      "What makes this tree different from other trees?",
      "How does this tree change through the seasons?"
    ]
  };
};